import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	FormGroup,
	FormText,
	Label
} from 'reactstrap';

import { FormField, tcFormValidators, FIELD_TYPES } from '@techchange/components';
import {
	DangerAlert,
	Loading,
	useLanguageContext,
	TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP_V4,
} from '@techchange/components';
import { messages } from '../../../../i18n';

import { RESOURCE_TYPES } from '../../../../constants';
import ContentEditor from '../../ContentEditor';
import {
	getInitialLanguage,
	useResourceFormData,
	RESOURCE_FORM_FIELDS,
} from '../../hooks/useResourceFormData';
import { BOOTSTRAP_ASPECT_RATIOS_V4_TO_V5 } from '../../utils/constants';
import { getAspectRatioOptions } from '../../utils/fieldOptions';
import parseEmbedSrc from '../../utils/parseEmbedSrc';
import { isSafeUrl } from '../../../../utils/urls';
import { contentBuilderPropTypes, embedDataShape, initialValuesShape } from '../../utils/propTypes';

import './ContentEditor.scss';

const axiosClient = axios.create({
	withCredentials: true,
});

const propTypes = {


	...contentBuilderPropTypes.contentEditor,

	/**
	 * The Resource type.
	 */
	type: PropTypes.oneOf([RESOURCE_TYPES.EMBED]).isRequired,

	/**
	 * The initial values of the form.
	 */
	initialValues: PropTypes.shape({

		...initialValuesShape,

		/**
		 * The content data.
		 */
		data: PropTypes.shape(embedDataShape),

	}).isRequired,

	/**
	 * The scope of this content, i.e. "session", "course", "organization", etc.
	 */
	scope: PropTypes.string.isRequired,

	/**
	 * The scope ID of this content.
	 */
	scopeId: PropTypes.string.isRequired,

};

const DEFAULT_RESOURCE_CONTENT = {
	[RESOURCE_FORM_FIELDS.TITLE]: '',
	[RESOURCE_FORM_FIELDS.DATA]: {
		aspectRatio: TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP_V4,
		articulate: false,
	},
};

const getInitialFormData = (props) => {

	const { title, data, width, resourceContents, deletedResourceContents } = props.initialValues;

	return {
		[RESOURCE_FORM_FIELDS.TITLE]: title || '',
		[RESOURCE_FORM_FIELDS.DATA]: {
			articulate: Boolean(data?.articulate) || false,
			src: data?.src,
			aspectRatio: data?.aspectRatio || TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP_V4,
		},
		[RESOURCE_FORM_FIELDS.WIDTH]: width,
		[RESOURCE_FORM_FIELDS.LANGUAGE]: getInitialLanguage(props),
		resource_contents: resourceContents || [{
			[RESOURCE_FORM_FIELDS.LANGUAGE]: props.defaultLanguage,
			...DEFAULT_RESOURCE_CONTENT,
		}],
		deleted_resource_contents: deletedResourceContents || [],
	};

};

const getInitialInputValidation = () => ({
	[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
	[RESOURCE_FORM_FIELDS.DATA]: {
		src: [tcFormValidators.required, tcFormValidators.url],
	},
	resource_contents: [tcFormValidators.arrayContainingObjects(RESOURCE_FORM_FIELDS.LANGUAGE, {
		[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
		[RESOURCE_FORM_FIELDS.DATA]: {
			src: [tcFormValidators.required, tcFormValidators.url],
		},
	})],
});

/**
 * Editor for "embed" resource types. Admins can makes changes to the
 * "title", "data.src" and "data.aspectRatio" fields for embed resources.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const EmbedContentEditor = (props) => {

	const {
		id,
		type,
		scope,
		scopeId,
		onCancel,
	} = props;

	const intl = useIntl();

	const { preferredLanguage, defaultLanguage } = useLanguageContext();

	const formProps = {
		...props,
		userLanguage: preferredLanguage,
		defaultLanguage,
	};

	const { formData, isSubmitting, inputValidation, actions } = useResourceFormData(
		() => getInitialFormData(formProps),
		getInitialInputValidation,
		{ ...formProps, defaultResourceContent: DEFAULT_RESOURCE_CONTENT }
	);

	const [isUploadingArticulate, setIsUploadingArticulate] = useState(false);
	const [showArticulateUploadError, setShowArticulateUploadError] = useState(false);

	const fileInputRef = useRef();
	const articulateUploadErrorRef = useRef();

	useEffect(() => {

		if (showArticulateUploadError) {
			articulateUploadErrorRef.current.scrollIntoView();
		}

	}, [showArticulateUploadError]);

	/**
	 * Attempts to upload file input data for Articulates. If successful, the "src"
	 * form field is then updated with the returned URL, otherwise an error is shown.
	 *
	 * Folders that do not contain articulate files are ignored.
	 */
	const handleOnArticulateUpload = async () => {

		// Check if we need to upload the articulate files and generate the embed URL automatically...
		if (!isUploadingArticulate &&
			fileInputRef.current?.files?.length > 0) {

			setIsUploadingArticulate(true);
			setShowArticulateUploadError(false);

			const articulateFileData = new FormData();

			for (let i = 0, len = fileInputRef.current.files.length; i < len; i++) {
				articulateFileData.append('tc-articulate-upload', fileInputRef.current.files[i]);
			}

			const url = `/api/v2/articulates/upload?scope=${scope}&scope_id=${scopeId}`;

			try {

				const response = await axiosClient.post(url, articulateFileData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});

				const articulateUploadURL = response.data;

				if (articulateUploadURL) {

					actions.updateField(RESOURCE_FORM_FIELDS.DATA, {
						src: articulateUploadURL,
						articulate: true,
					});

				} else {

					actions.updateField('articulate', false);

				}

			} catch (error) {

				setShowArticulateUploadError(true);

			}

			setIsUploadingArticulate(false);

		}

	};

	const activeTranslation = formData.resource_contents
		.find((rContent) => rContent.language === formData.language);

	const activeInputValidation = inputValidation?.resource_contents?.data?.[formData.language];

	const activeAspectRatio =
		BOOTSTRAP_ASPECT_RATIOS_V4_TO_V5[activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.aspectRatio];

	const formFields = activeTranslation && (
		<>
			<FormField
				type={FIELD_TYPES.TEXT}
				name={RESOURCE_FORM_FIELDS.TITLE}
				value={activeTranslation?.[RESOURCE_FORM_FIELDS.TITLE]}
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_LABEL_TEXT_TITLE)}
				placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_LABEL_TEXT_TITLE)}
				isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.invalid}
				error={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.error}
				isEditing
				isRequired
				onChange={actions.updateField} />
			<FormField
				type={FIELD_TYPES.TEXT}
				name="src"
				value={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src}
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_LABEL_TEXT_SRC)}
				placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_PLACEHOLDER_TEXT_SRC)}
				isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.src?.invalid}
				error={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.src?.error}
				isEditing
				isRequired
				onChange={(name, value) => actions.updateField(name, parseEmbedSrc(value))} />
			{isSafeUrl(activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src) &&
				<div
					className={`
						ratio
						ratio-${activeAspectRatio}
						mb-3
					`}>
					<iframe
						src={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src}
						title={activeTranslation?.[RESOURCE_FORM_FIELDS.TITLE]}
						frameBorder="0"
						allowFullScreen />
				</div>
			}
			<FormField
				type={FIELD_TYPES.SELECT}
				name="aspectRatio"
				value={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.aspectRatio}
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_LABEL_TEXT_ASPECT_RATIO)}
				help={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_HELP_TEXT_ASPECT_RATIO)}
				options={getAspectRatioOptions(intl)}
				isEditing
				onChange={actions.updateField} />
		</>
	);

	const advancedFormFields = activeTranslation && (
		<>
			<FormGroup>
				<Label for="articulate-upload">
					<FormattedMessage
						{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_LABEL_TEXT_ARTICULATE_UPLOAD} />
				</Label>
				<div>
					<input
						ref={fileInputRef}
						type="file"
						name="articulate-upload"
						webkitdirectory=""
						multiple
						onChange={handleOnArticulateUpload} />
				</div>
				<FormText>
					<FormattedMessage
						{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_FIELD_HELP_TEXT_ARTICULATE_UPLOAD} />
				</FormText>
			</FormGroup>
		</>
	);

	return (
		<div className="embed-editor-container">
			{showArticulateUploadError &&
				<div ref={articulateUploadErrorRef}>
					<DangerAlert>
						<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_EMBED_EDITOR_MSG_TEXT_ARTICULATE_UPLOAD_ERROR} />
					</DangerAlert>
				</div>
			}
			{isUploadingArticulate &&
				<div className="articulate-loading-overlay">
					<Loading
						className="loading-spinner"
						isLoading />
				</div>
			}
			<ContentEditor
				id={id}
				type={type}
				formData={formData}
				isSubmitting={isSubmitting}
				inputValidation={inputValidation}
				actions={actions}
				formFields={formFields}
				advancedFormFields={advancedFormFields}
				onCancel={onCancel} />
		</div>
	);

};

EmbedContentEditor.propTypes = propTypes;

export default EmbedContentEditor;
